import React from 'react';
import { motion } from 'framer-motion';
import { EnrichedRiskArea } from '../../types/prevention';
import { RISK_LEVEL_CONFIG, STATUS_CONFIG, getRiskBand } from '../RiskAreasWidget';
import { AlertTriangle, Clock, CheckCircle2, MoreHorizontal, Eye } from 'lucide-react';
import { SifBadge, DataConfidenceBadge, ScheduledDateBadge, hasDataGap } from './shared';
interface Props {
  areas: EnrichedRiskArea[];
  onSelect: (area: EnrichedRiskArea) => void;
}
export function VariantEKanban({
  areas,
  onSelect
}: Props) {
  const columns = [{
    id: 'open',
    title: 'Open',
    icon: AlertTriangle,
    color: 'text-red-600',
    bg: 'bg-red-50'
  }, {
    id: 'measures_taken',
    title: 'Measures Taken',
    icon: Clock,
    color: 'text-amber-600',
    bg: 'bg-amber-50'
  }, {
    id: 'monitoring',
    title: 'Monitoring',
    icon: Eye,
    color: 'text-sky-600',
    bg: 'bg-sky-50'
  }, {
    id: 'closed',
    title: 'Closed',
    icon: CheckCircle2,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50'
  }];
  return <div className="flex h-full gap-6 p-6 overflow-x-auto bg-gray-50/50" data-id="element-2976">
      {columns.map(col => {
      const colAreas = areas.filter(a => a.status === col.id);
      return <div key={col.id} className="flex-1 min-w-[320px] flex flex-col max-w-md" data-id="element-2977">
            {/* Column Header */}
            <div className={`flex items-center justify-between p-3 rounded-t-xl border-b-2 ${col.bg} border-gray-200 mb-4`} data-id="element-2978">
              <div className="flex items-center gap-2" data-id="element-2979">
                <col.icon size={18} className={col.color} data-id="element-2980" />
                <h3 className="font-bold text-gray-900" data-id="element-2981">{col.title}</h3>
              </div>
              <span className="bg-white text-gray-600 text-xs font-bold px-2 py-1 rounded-full shadow-sm" data-id="element-2982">
                {colAreas.length}
              </span>
            </div>

            {/* Column Cards */}
            <div className="flex-1 overflow-y-auto space-y-3 pb-4" data-id="element-2983">
              {colAreas.map(area => {
            const config = RISK_LEVEL_CONFIG[area.riskLevel];
            const band = getRiskBand(area.riskScore);
            const dataGap = hasDataGap(area);
            let cardClass = 'w-full text-left bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-indigo-300 transition-all group';
            if (area.sifPotential) {
              cardClass = 'w-full text-left bg-red-50/60 p-4 rounded-xl shadow-sm border border-red-300 hover:shadow-md hover:border-red-400 transition-all group border-l-4 border-l-red-700';
            } else if (dataGap) {
              cardClass = 'w-full text-left bg-amber-50/30 p-4 rounded-xl shadow-sm border border-dashed border-amber-300 hover:shadow-md hover:border-amber-400 transition-all group border-l-4 border-l-amber-400';
            }
            return <motion.button key={area.id} layoutId={`kanban-${area.id}`} onClick={() => onSelect(area)} className={cardClass} data-id="element-2984">
                    <div className="flex items-start justify-between mb-3" data-id="element-2985">
                      <div className={`px-2 py-1 rounded ${config.bgColor}`} data-id="element-2986">
                        <span className={`text-[10px] font-bold uppercase tracking-wide ${config.color}`} data-id="element-2987">
                          {band.short} · {area.riskScore}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-2988">
                        <DataConfidenceBadge confidence={area.dataConfidence} data-id="element-2989" />
                        {area.sifPotential && <SifBadge data-id="element-2990" />}
                      </div>
                    </div>

                    <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2991">
                      {area.project}
                    </h4>
                    <div className="flex flex-col gap-1.5 mb-4" data-id="element-2992">
                      <p className="text-xs text-gray-500 line-clamp-2" data-id="element-2993">
                        {area.floor} · {area.location}
                      </p>
                      <div data-id="element-2994">
                        <ScheduledDateBadge date={area.scheduledDate} data-id="element-2995" />
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100" data-id="element-2996">
                      <span className="text-[10px] text-gray-400 font-medium" data-id="element-2997">
                        {area.contractors.length} Contractor
                        {area.contractors.length !== 1 ? 's' : ''}
                      </span>
                      <MoreHorizontal size={16} className="text-gray-300 group-hover:text-indigo-400 transition-colors" data-id="element-2998" />
                    </div>
                  </motion.button>;
          })}

              {colAreas.length === 0 && <div className="p-8 text-center border-2 border-dashed border-gray-200 rounded-xl text-gray-400 text-sm font-medium" data-id="element-2999">
                  No activities
                </div>}
            </div>
          </div>;
    })}
    </div>;
}